import { isNumber } from 'radash';

import { ZoneHelpers } from '@diagram/helpers/ZoneHelpers';

import {
    DiagramComponentInstance,
    DiagramConnection,
    PortControlMethod,
    PortControlMethods,
    VoltageType,
    Zone,
    ZoneValidationError,
    ZoneValidationErrorType,
    ZoneValidator,
} from 'models';

type Port = {
    componentInstance: DiagramComponentInstance;
    lines: DiagramConnection['lines'];
    voltageType: DiagramComponentInstance['configuration']['ports'][number]['voltageType'];
    type: 'voltageSource' | PortControlMethod;
};

type ZoneControl = {
    zone: Zone;
    voltageType: VoltageType | null;
    ports: Port[];
    activeLines: DiagramConnection['lines'];
};

const zoneControlMethodsValidator: ZoneValidator['validator'] = ({ diagram, zones }) => {
    const errors: ZoneValidationError[] = [];

    const voltageSources = ['battery', 'utility'];

    const voltageControlMethods: PortControlMethods = [
        'constant-voltage',
        'droop-voltage',
        'power-voltage',
        'constant-voltage-frequency',
        'maximum-power-point-tracking',
    ];

    const zoneControls = zones.map((zone): ZoneControl => {
        let voltageType: VoltageType | null = null;

        const ports: Port[] = [];

        const activeLines: DiagramConnection['lines'] = {
            AC: {
                L1: false,
                L2: false,
                L3: false,
                N: false,
                PE: false,
            },
            DC: {
                'L+': false,
                'M': false,
                'L-': false,
                'PE': false,
            },
        };

        zone.connections.forEach((connection) => {
            const { from, to, lines } = connection;

            const fromComponentInstance = diagram.componentInstances[from.componentInstanceId];
            const toComponentInstance = diagram.componentInstances[to.componentInstanceId];

            const fromVoltageType = isNumber(from.port)
                ? fromComponentInstance.configuration.ports[from.port]?.voltageType
                : null;
            const toVoltageType = isNumber(to.port)
                ? toComponentInstance.configuration.ports[to.port]?.voltageType
                : null;

            const fromControlMethod = isNumber(from.port)
                ? fromComponentInstance.configuration.ports[from.port]?.controlMethod
                : undefined;
            const toControlMethod = isNumber(to.port)
                ? toComponentInstance.configuration.ports[to.port]?.controlMethod
                : undefined;

            if (fromVoltageType === 'AC' && toVoltageType === 'AC') {
                activeLines.AC = {
                    L1: activeLines.AC.L1 || lines.AC.L1,
                    L2: activeLines.AC.L2 || lines.AC.L2,
                    L3: activeLines.AC.L3 || lines.AC.L3,
                    N: activeLines.AC.N || lines.AC.N,
                    PE: activeLines.AC.PE || lines.AC.PE,
                };

                voltageType = 'AC';
            } else if (fromVoltageType === 'DC' && toVoltageType === 'DC') {
                activeLines.DC = {
                    'L+': activeLines.DC['L+'] || lines.DC['L+'],
                    'M': activeLines.DC.M || lines.DC.M,
                    'L-': activeLines.DC['L-'] || lines.DC['L-'],
                    'PE': activeLines.DC.PE || lines.DC.PE,
                };

                voltageType = 'DC';
            }

            const fromPurpose =
                isNumber(from.port) && fromVoltageType
                    ? // @ts-expect-error Types on electrical ports fail to be inferred
                      fromComponentInstance.specifications.electrical.ports?.[from.port]?.[fromVoltageType]?.purpose
                    : undefined;

            if (voltageSources.includes(fromComponentInstance.componentType) || voltageSources.includes(fromPurpose)) {
                if (fromControlMethod !== 'power-voltage') {
                    ports.push({
                        componentInstance: fromComponentInstance,
                        lines,
                        voltageType: fromVoltageType,
                        type: 'voltageSource',
                    });
                }
            }

            const toPurpose =
                isNumber(to.port) && toVoltageType
                    ? // @ts-expect-error Types on electrical ports fail to be inferred
                      toComponentInstance.specifications.electrical.ports?.[to.port]?.[toVoltageType]?.purpose
                    : undefined;

            if (voltageSources.includes(toComponentInstance.componentType) || voltageSources.includes(toPurpose)) {
                if (toControlMethod !== 'power-voltage') {
                    ports.push({
                        componentInstance: toComponentInstance,
                        lines,
                        voltageType: toVoltageType,
                        type: 'voltageSource',
                    });
                }
            }

            if (isNumber(from.port) && fromControlMethod && voltageControlMethods.includes(fromControlMethod)) {
                ports.push({
                    componentInstance: fromComponentInstance,
                    lines,
                    voltageType: fromVoltageType,
                    type: fromControlMethod,
                });
            }

            if (isNumber(to.port) && toControlMethod && voltageControlMethods.includes(toControlMethod)) {
                ports.push({
                    componentInstance: toComponentInstance,
                    lines,
                    voltageType: toVoltageType,
                    type: toControlMethod,
                });
            }
        });

        return { zone, voltageType, ports, activeLines };
    });

    const zonesWithVoltageSet = findZonesWithVoltageSet(zoneControls);

    zoneControls.forEach(({ zone, voltageType, ports, activeLines }) => {
        const portsWithConstantVoltage = ports.filter(
            ({ type }) =>
                type === 'voltageSource' ||
                type === 'constant-voltage' ||
                type === 'constant-voltage-frequency' ||
                type === 'maximum-power-point-tracking',
        );

        const portsWithDroopVoltage = ports.filter(({ type }) => type === 'power-voltage' || type === 'droop-voltage');

        if (voltageType === 'AC') {
            if (!zonesWithVoltageSet.includes(zone)) {
                ZoneHelpers.error(errors, ZoneValidationErrorType.CONTROL_METHODS_NO_CONTROL_COMPONENT, zone);
            } else if (portsWithConstantVoltage.length > 1) {
                ZoneHelpers.error(
                    errors,
                    ZoneValidationErrorType.CONTROL_METHODS_MULTIPLE_VOLTAGE_CONTROL_COMPONENTS,
                    zone,
                );
            } else if (portsWithConstantVoltage.length > 0 && portsWithDroopVoltage.length > 0) {
                ZoneHelpers.error(
                    errors,
                    ZoneValidationErrorType.CONTROL_METHODS_NO_CV_AND_DV_COMBINED,
                    zone,
                    ports.map(({ componentInstance }) => componentInstance),
                );
            }
        } else if (voltageType === 'DC') {
            const countConstantVoltage = portsWithConstantVoltage.reduce(
                (acc, { lines }) => ({
                    'L+': acc['L+'] + +lines.DC['L+'],
                    'M': acc.M + +lines.DC.M,
                    'L-': acc['L-'] + +lines.DC['L-'],
                }),
                {
                    'L+': 0,
                    'M': 0,
                    'L-': 0,
                },
            );

            const countDroopVoltage = portsWithDroopVoltage.reduce(
                (acc, { lines }) => ({
                    'L+': acc['L+'] + +lines.DC['L+'],
                    'M': acc.M + +lines.DC.M,
                    'L-': acc['L-'] + +lines.DC['L-'],
                }),
                {
                    'L+': 0,
                    'M': 0,
                    'L-': 0,
                },
            );

            const countTotal = {
                'L+': countConstantVoltage['L+'] + countDroopVoltage['L+'],
                'M': countConstantVoltage.M + countDroopVoltage.M,
                'L-': countConstantVoltage['L-'] + countDroopVoltage['L-'],
            };

            if (activeLines.DC.M && countTotal.M === 0) {
                ZoneHelpers.error(errors, ZoneValidationErrorType.CONTROL_METHODS_NO_CONTROL_COMPONENT_M, zone);
            }

            if (activeLines.DC['L+'] && countTotal['L+'] === 0) {
                ZoneHelpers.error(errors, ZoneValidationErrorType.CONTROL_METHODS_NO_CONTROL_COMPONENT_Lp, zone);
            }

            if (activeLines.DC['L-'] && countTotal['L-'] === 0) {
                ZoneHelpers.error(errors, ZoneValidationErrorType.CONTROL_METHODS_NO_CONTROL_COMPONENT_Ln, zone);
            }

            if (countConstantVoltage['L+'] > 1 && countConstantVoltage.M > 1) {
                ZoneHelpers.error(
                    errors,
                    ZoneValidationErrorType.CONTROL_METHODS_MULTIPLE_VOLTAGE_CONTROL_COMPONENTS_Lp_M,
                    zone,
                );
            } else if (countConstantVoltage['L-'] > 1 && countConstantVoltage.M > 1) {
                ZoneHelpers.error(
                    errors,
                    ZoneValidationErrorType.CONTROL_METHODS_MULTIPLE_VOLTAGE_CONTROL_COMPONENTS_M_Ln,
                    zone,
                );
            } else if (countConstantVoltage['L+'] > 1 && countConstantVoltage['L-'] > 1) {
                ZoneHelpers.error(
                    errors,
                    ZoneValidationErrorType.CONTROL_METHODS_MULTIPLE_VOLTAGE_CONTROL_COMPONENTS_Lp_Ln,
                    zone,
                );
            } else if (
                countConstantVoltage['L+'] > 0 &&
                countConstantVoltage.M > 0 &&
                countDroopVoltage['L+'] > 0 &&
                countDroopVoltage.M > 0
            ) {
                ZoneHelpers.error(errors, ZoneValidationErrorType.CONTROL_METHODS_NO_CV_AND_DV_COMBINED_Lp_M, zone);
            } else if (
                countConstantVoltage.M > 0 &&
                countConstantVoltage['L-'] > 0 &&
                countDroopVoltage.M > 0 &&
                countDroopVoltage['L-'] > 0
            ) {
                ZoneHelpers.error(errors, ZoneValidationErrorType.CONTROL_METHODS_NO_CV_AND_DV_COMBINED_M_Ln, zone);
            } else if (
                countConstantVoltage['L+'] > 0 &&
                countConstantVoltage['L-'] > 0 &&
                countDroopVoltage['L+'] > 0 &&
                countDroopVoltage['L-'] > 0
            ) {
                ZoneHelpers.error(errors, ZoneValidationErrorType.CONTROL_METHODS_NO_CV_AND_DV_COMBINED_Lp_Ln, zone);
            }
        }
    });

    return errors;
};

const findZonesWithVoltageSet = (zoneControls: ZoneControl[]) => {
    const zones = zoneControls.map(({ zone }) => zone);
    const zonesWithVoltageSet = zoneControls.filter(({ ports }) => ports.length > 0).map(({ zone }) => zone);

    for (const zone of zonesWithVoltageSet) {
        const transformerIds = zone.componentInstances
            .filter(({ componentType }) => componentType === 'transformer')
            .map(({ id }) => id);

        const zonesFedThroughTransformer = zones.filter(
            (neighbour) =>
                !zonesWithVoltageSet.includes(neighbour) &&
                neighbour.componentInstances.some(({ id }) => transformerIds.includes(id)),
        );

        zonesWithVoltageSet.push(...zonesFedThroughTransformer);
    }

    return zonesWithVoltageSet;
};

export { zoneControlMethodsValidator };
